import cloneDeep from 'lodash/cloneDeep';

import { HttpHeaders } from '@angular/common/http';
import { effect, Inject, Injectable, DOCUMENT } from '@angular/core';

import { Store } from '@ngrx/store';
import { SetWhiteLabel } from './white-label-actions';

import { RebrandService } from 'src/app/core/services/rebrand.service';
import { ResponseErrorService } from '../errors/response-error.service';

import { WhiteLabelApiProviderService } from '@core/api';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { UpdateWorkspaceLogo } from '../workspace/workspace.actions';
import { EPdfLogoTarget } from './pdfLogo-targets-enum';
import { clearWhiteLabel } from './white-label-utils/clear-white-label';
import { getDefaultFontFamily } from './white-label-utils/get-default-font-family';
import { getDefaultPanelColors } from './white-label-utils/get-default-panel-colors';
import { getDefaultPrimaryColors } from './white-label-utils/get-default-primary-colors';
import { setDefaultStyle } from './white-label-utils/set-default-style';
import { setWhiteLabel } from './white-label-utils/set-white-label';
import { setWhiteLabelStyles } from './white-label-utils/set-white-label-styles';
import { TWhiteLabel, TWhiteLabelStore } from './white-label.model';

type TWhiteLabelInput = Partial<TWhiteLabelStore> & { isDefaultLabel?: boolean };

@Injectable({
  providedIn: 'root',
})
export class WhiteLabelService {
  whiteLabelStore: TWhiteLabelStore = {
    data: {},
    created: false,
    accountId: null,
  };

  whiteLabels: {
    [accountId: string]: TWhiteLabel;
  } = {};

  private themeWrapper: HTMLBodyElement = this.document.querySelector('body');
  private refetch = {};
  private lastWhiteLabelInput: TWhiteLabelInput | null = null;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private store: Store<{
      whiteLabel: TWhiteLabelStore;
    }>,
    private responseErrorService: ResponseErrorService,
    private whiteLabelApiProviderService: WhiteLabelApiProviderService,
    private rebrandService: RebrandService,
  ) {
    this.setDefaultStyle();
    this.resolveStylesOnRebrandChange();
  }

  getWhiteLabelStore(): TWhiteLabelStore {
    return this.whiteLabelStore;
  }

  setWhiteLabel({
    data = undefined,
    accountId = undefined,
    isDefaultLabel = false,
  }: TWhiteLabelInput = {}): TWhiteLabelStore {
    this.lastWhiteLabelInput = { data, accountId, isDefaultLabel };

    this.whiteLabelStore = setWhiteLabel({
      data,
      accountId,
      oldWhiteLabel: this.whiteLabelStore,
      isDefaultLabel: isDefaultLabel,
    });

    setWhiteLabelStyles(this.themeWrapper, this.whiteLabelStore);

    this.store.dispatch(
      new SetWhiteLabel({
        whiteLabel: this.whiteLabelStore,
      }),
    );

    return this.getWhiteLabelStore();
  }

  clearWhiteLabel(): TWhiteLabelStore {
    this.whiteLabelStore = clearWhiteLabel(this.whiteLabelStore);

    this.store.dispatch(
      new SetWhiteLabel({
        whiteLabel: this.whiteLabelStore,
      }),
    );

    return this.getWhiteLabelStore();
  }

  fetchWhiteLabel(accountId: string, refetch: boolean = false): void {
    this.whiteLabelStore = cloneDeep(this.whiteLabelStore);

    if (this.whiteLabels[accountId] && !refetch) {
      const whiteLabel = this.whiteLabels[accountId];

      if (whiteLabel) {
        this.setWhiteLabel({
          data: whiteLabel,
          accountId,
        });
      } else {
        this.setWhiteLabel({
          data: this.getDefaultWhiteLabel(accountId),
          accountId,
          isDefaultLabel: true,
        });
      }

      return;
    }

    this.whiteLabelApiProviderService
      .fetchWhiteLabel(accountId, this.refetch[accountId])
      .pipe(
        tap((response: TWhiteLabel) => {
          this.refetch[accountId] = false;

          if (Object.keys(response).length) {
            this.setWhiteLabel({
              data: response || this.getDefaultWhiteLabel(accountId),
              accountId,
            });
          } else {
            this.setWhiteLabel({
              data: this.getDefaultWhiteLabel(accountId),
              accountId,
              isDefaultLabel: true,
            });
          }
        }),
        catchError((error) => {
          this.responseErrorService.handleRequestError(error, 'Failed to fetch white label');
          return of(null);
        }),
      )
      .subscribe();
  }

  createWhiteLabel(skin: TWhiteLabel, accountId: string): Observable<TWhiteLabel> {
    return this.whiteLabelApiProviderService.createWhiteLabel(accountId, skin).pipe(
      tap((response) => {
        this.refetch[accountId] = true;

        this.setWhiteLabel({
          data: response || {},
          accountId,
        });
      }),
      catchError((error) => {
        this.responseErrorService.handleRequestError(error, 'Failed to create white label');
        return of(null);
      }),
    );
  }

  updateWhiteLabel(
    skin: TWhiteLabel,
    accountId: string,
    {
      update = true,
    }: {
      update?: boolean;
    } = {},
  ): Observable<TWhiteLabel> {
    return this.whiteLabelApiProviderService.updateWhiteLabel(accountId, skin).pipe(
      tap((response) => {
        if (update) {
          this.refetch[accountId] = true;

          this.setWhiteLabel({
            data: response || {},
            accountId,
          });
        }
      }),
      catchError((error) => {
        this.responseErrorService.handleRequestError(error, 'Failed to update white label');
        return of(null);
      }),
    );
  }

  uploadLogo(accountId: string, image: File): Observable<string> {
    const formData = new FormData();
    const headers = new HttpHeaders();

    formData.append('image_file', image);
    headers.append('Content-Type', undefined);

    return this.whiteLabelApiProviderService.uploadLogo(accountId, formData).pipe(
      tap(() => {
        this.refetch[accountId] = true;
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to upload logo'),
      ),
    );
  }

  uploadPdfLogo(targetId: string, image: File, targetType: string): Observable<string> {
    const formData = new FormData();
    const headers = new HttpHeaders();

    formData.append('image_file', image);
    headers.append('Content-Type', undefined);

    return this.whiteLabelApiProviderService.uploadLogo(targetId, formData).pipe(
      tap((imageId) => {
        this.whiteLabelApiProviderService
          .uploadPdfLogo(targetId, targetType, imageId)
          .pipe(
            tap(() => {
              this.setSiteLogo(imageId, targetId);

              if (targetType === EPdfLogoTarget.ACCOUNT) {
                this.refetch[targetId] = true;
              }
            }),
            map(() => imageId),
            catchError((error) =>
              this.responseErrorService.handleRequestError(error, 'Failed to upload pdf logo'),
            ),
          )
          .subscribe();
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to upload logo'),
      ),
    );
  }

  deletePdfLogo(targetId: string, targetType: string): Observable<string> {
    return this.whiteLabelApiProviderService.deletePdfLogo(targetId, targetType).pipe(
      tap(() => {
        if (targetType === EPdfLogoTarget.ACCOUNT) {
          this.refetch[targetId] = true;
        }
      }),
      catchError((error) =>
        this.responseErrorService.handleRequestError(error, 'Failed to delete pdf logo'),
      ),
    );
  }

  setDefaultStyle(): void {
    this.lastWhiteLabelInput = null;

    setDefaultStyle(this.themeWrapper);

    this.clearWhiteLabel();
  }

  private resolveStylesOnRebrandChange(): void {
    effect(() => {
      this.rebrandService.rebrandEnabled();

      this.resolveStyles();
    });
  }

  private resolveStyles(): void {
    if (!this.lastWhiteLabelInput) {
      this.setDefaultStyle();

      return;
    }

    const { accountId, isDefaultLabel } = this.lastWhiteLabelInput;

    if (isDefaultLabel) {
      this.setWhiteLabel({
        data: this.getDefaultWhiteLabel(accountId),
        accountId,
        isDefaultLabel,
      });

      return;
    }

    this.setWhiteLabel(this.lastWhiteLabelInput);
  }

  getDefaultWhiteLabel(accountId: string): TWhiteLabel {
    return {
      logoDark: undefined,
      logoLight: undefined,
      accountRefId: accountId,
      fontFamily: getDefaultFontFamily(),
      ...getDefaultPanelColors(),
      ...getDefaultPrimaryColors(),
    };
  }

  setSiteLogo(imageId: string, workspaceId: string): void {
    this.store.dispatch(
      new UpdateWorkspaceLogo({
        workspaceId,
        imageId,
      }),
    );
  }
}
